import { useCallback, useState } from 'react'
import { useInterval } from 'react-use'
import { getMarketplaceBid } from 'utils/api/marketplace'
import { useMarketplaceContext } from 'utils/context/MarketplaceContext'
import { BidResponse } from 'apps/fleet-planner/types'

const MAX_POLL_TIME_MS = 3500
const POLL_INTERVAL_MS = 500

export const useBidSubmit = () => {
  const { getMarketplaceLoad } = useMarketplaceContext()

  const [updatedBid, setUpdatedBid] = useState<BidResponse | undefined>()
  const isAccepted = updatedBid?.status === 'accepted'
  const isPending = updatedBid?.status === 'pending'
  const isCountered = updatedBid?.status === 'countered'
  const isUpdated = updatedBid !== undefined

  const [pollAttempts, setPollAttempts] = useState<number>(0)
  const [pollInterval, setPollInterval] = useState<number | null>(null)

  const stopPolling = useCallback(() => {
    setPollAttempts(Infinity)
    setPollInterval(null)
  }, [setPollAttempts, setPollInterval])

  const clearUpdatedBid = useCallback(() => {
    setUpdatedBid(undefined)
    stopPolling()
  }, [setUpdatedBid, stopPolling])

  const bidUpdateSuccessHandler = (shipmentId: string, bidId?: number) => {
    if (bidId) {
      getMarketplaceBid(bidId).then(response => {
        setUpdatedBid(response?.bid || undefined)
        setPollAttempts(0)
        setPollInterval(POLL_INTERVAL_MS)
      })
    } else {
      clearUpdatedBid()
    }
    getMarketplaceLoad(shipmentId)
  }

  // Short polls marketplace for bid state changes
  useInterval(() => {
    if (!updatedBid?.id || pollAttempts * POLL_INTERVAL_MS >= MAX_POLL_TIME_MS) {
      // We're done polling if there isn't a bid or we've exceeded the poll attempt limit
      clearUpdatedBid()
    } else {
      getMarketplaceBid(updatedBid.id)
        .then(response => {
          const bid = response?.bid || undefined
          if (bid.status !== 'pending') {
            stopPolling()
          }
          setUpdatedBid(bid)
        })
        .catch(() => clearUpdatedBid())
        .finally(() => setPollAttempts(count => count + 1))
    }
  }, pollInterval)

  return {
    bidUpdateSuccessHandler,
    updatedBid,
    clearUpdatedBid,
    isAccepted,
    isCountered,
    isPending,
    isUpdated,
  }
}
